import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { meetingAnalyticsApi } from '../api/meetingAnalytics';
import PremiumLayout from '../components/PremiumLayout';
import { 
  ChartBarIcon,
  CalendarIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

const MeetingAnalytics: React.FC = () => {
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
  });

  const { data: analytics, isLoading } = useQuery({
    queryKey: ['meeting-analytics', filters],
    queryFn: () => meetingAnalyticsApi.getAnalytics({
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
    }),
  });

  if (isLoading) {
    return (
      <PremiumLayout title="Meeting Analytics">
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-200 border-t-indigo-600"></div>
        </div>
      </PremiumLayout>
    );
  }

  if (!analytics) {
    return (
      <PremiumLayout title="Meeting Analytics">
        <div className="text-center py-12">
          <p className="text-gray-500">No analytics data available</p>
        </div>
      </PremiumLayout>
    );
  }

  return (
    <PremiumLayout
      title="Meeting Analytics"
      subtitle="Analyze meeting performance and trends"
    >
      <div className="space-y-6">
        {/* Filters */}
        <div className="premium-card p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Start Date
              </label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                End Date
              </label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="premium-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Meetings</p>
                <p className="text-2xl font-bold text-gray-900">{analytics.totalMeetings}</p>
              </div>
              <CalendarIcon className="h-8 w-8 text-indigo-500" />
            </div>
          </div>
          <div className="premium-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Held Meetings</p>
                <p className="text-2xl font-bold text-gray-900">{analytics.heldMeetings}</p>
              </div>
              <CheckCircleIcon className="h-8 w-8 text-green-500" />
            </div>
          </div>
          <div className="premium-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">No Shows</p>
                <p className="text-2xl font-bold text-gray-900">{analytics.noShowMeetings}</p>
              </div>
              <XCircleIcon className="h-8 w-8 text-red-500" />
            </div>
          </div>
          <div className="premium-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">No-Show Rate</p>
                <p className="text-2xl font-bold text-gray-900">
                  {analytics.noShowRate.toFixed(1)}%
                </p>
              </div>
              <ChartBarIcon className="h-8 w-8 text-yellow-500" />
            </div>
          </div>
        </div>

        {/* Detailed Analytics */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Meetings by Stage */}
          <div className="premium-card p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Meetings by Stage</h3>
            <div className="space-y-2">
              {Object.entries(analytics.meetingsByStage || {}).map(([stage, count]) => (
                <div key={stage} className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 capitalize">{stage}</span>
                  <span className="text-sm font-semibold text-gray-900">{count as number}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Meetings by Medium */}
          <div className="premium-card p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Meetings by Medium</h3>
            <div className="space-y-2">
              {Object.entries(analytics.meetingsByMedium || {}).map(([medium, count]) => (
                <div key={medium} className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 capitalize">{medium}</span>
                  <span className="text-sm font-semibold text-gray-900">{count as number}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Average Duration */}
        {analytics.averageDuration > 0 && (
          <div className="premium-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Average Meeting Duration</h3>
                <p className="text-sm text-gray-600">Based on completed meetings</p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-indigo-600">
                  {Math.round(analytics.averageDuration)}
                </p>
                <p className="text-sm text-gray-600">minutes</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </PremiumLayout>
  );
};

export default MeetingAnalytics;
